import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DirectionsIcon from '@mui/icons-material/Directions';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import Header from '../components/Header';
import ImageCarousel from '../components/ImageCarousel';
import SaleBadge from '../components/SaleBadge';
import { useSaleById } from '../hooks/useSaleById';
import { useApp } from '../context/AppContext';
import { SaleType } from '../types';

const inferSaleType = (title: string | null, sourceWebsite: string | null): SaleType => {
  const titleLower = title?.toLowerCase() || '';
  const sourceLower = sourceWebsite?.toLowerCase() || '';
  
  if (titleLower.includes('estate') || sourceLower.includes('estate')) {
    return 'estate_sale';
  }
  if (titleLower.includes('multi-family') || titleLower.includes('multi family')) {
    return 'multi_family';
  }
  if (titleLower.includes('moving')) {
    return 'moving_sale';
  }
  if (titleLower.includes('fundraiser')) {
    return 'fundraiser';
  }
  
  return 'yard_sale';
};

const SaleDetailsScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useApp();
  
  const { data: sale, isLoading, error } = useSaleById(id || '');
  const saleType = sale ? inferSaleType(sale.title, sale.source_website) : 'yard_sale';

  const handleBack = () => {
    navigate(-1);
  };

  const handleDirections = () => {
    if (sale?.address) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        `${sale.address}, ${sale.city}, ${sale.state} ${sale.zip_code || ''}`
      )}`;
      window.open(url, '_blank');
    }
  };

  const handleAddToCalendar = () => {
    if (!sale) return;
    
    const startDate = new Date(sale.start_date);
    const endDate = sale.end_date ? new Date(sale.end_date) : new Date(startDate);
    
    const formatDate = (date: Date) => {
      return date.toISOString().replace(/-|:|\.\d{3}/g, '').slice(0, 15);
    };
    
    const startTime = sale.start_time ? sale.start_time.replace(':', '') : '0800';
    const endTime = sale.end_time ? sale.end_time.replace(':', '') : '1600';
    
    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(sale.title || 'Yard Sale')}&dates=${formatDate(startDate)}T${startTime}00/${formatDate(endDate)}T${endTime}00&location=${encodeURIComponent(`${sale.address}, ${sale.city}, ${sale.state}`)}&details=${encodeURIComponent(sale.description || '')}`;
    
    window.open(calendarUrl, '_blank');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !sale) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">Sale not found</Typography>
        <Button onClick={() => navigate('/home')} sx={{ mt: 2 }}>
          Go Home
        </Button>
      </Box>
    );
  }

  const photos = sale.sale_photos || [];

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header with Back Button */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <IconButton
          onClick={handleBack}
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: 'white' },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        
        <IconButton
          onClick={() => toggleFavorite(sale.id)}
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: 'white' },
          }}
        >
          {isFavorite(sale.id) ? (
            <FavoriteIcon sx={{ color: 'error.main' }} />
          ) : (
            <FavoriteBorderIcon />
          )}
        </IconButton>
      </Box>

      {/* Image Carousel */}
      <Box sx={{ height: 300, flexShrink: 0 }}>
        <ImageCarousel photos={photos} height={300} showDots />
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          mt: -4,
          position: 'relative',
          bgcolor: 'background.paper',
          pt: 3,
          px: 3,
        }}
      >
        {/* Badge */}
        <Box sx={{ mb: 2 }}>
          <SaleBadge saleType={saleType} />
        </Box>

        {/* Title */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {sale.title}
        </Typography>

        {/* Source Website */}
        <Chip
          label={sale.source_website}
          size="small"
          sx={{
            bgcolor: 'grey.100',
            color: 'text.secondary',
            fontWeight: 500,
            mb: 3,
          }}
        />

        {/* Location */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 3 }}>
          <LocationOnIcon sx={{ color: 'primary.main', mt: 0.25 }} />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {sale.address}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sale.city}, {sale.state} {sale.zip_code}
            </Typography>
          </Box>
        </Box>

        {/* Date & Time */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatDate(sale.start_date)}
                {sale.end_date && sale.end_date !== sale.start_date && (
                  <> - {formatDate(sale.end_date)}</>
                )}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Time
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatTime(sale.start_time)}
                {sale.end_time && ` - ${formatTime(sale.end_time)}`}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Description */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
          Description
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, mb: 4 }}
        >
          {sale.description || 'No description provided.'}
        </Typography>
      </Box>

      {/* Bottom Action Buttons */}
      <Box
        sx={{
          p: 3,
          pb: 4,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          startIcon={<EventAvailableIcon />}
          onClick={handleAddToCalendar}
          sx={{
            py: 1.5,
            borderRadius: 3,
            borderColor: 'divider',
            color: 'text.primary',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'primary.main',
              color: 'white',
            },
          }}
        >
          Add to Calendar
        </Button>
        
        <Button
          fullWidth
          variant="contained"
          startIcon={<DirectionsIcon />}
          onClick={handleDirections}
          sx={{
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
          }}
        >
          Get Directions
        </Button>
      </Box>
    </Box>
  );
};

export default SaleDetailsScreen;
